import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';

import actionTypes from 'constants/actionTypes';

import InputDate from 'modules/_shared/components/InputDate';
import InputNumber from 'modules/_shared/components/InputNumber';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import NotarySection from 'modules/_shared/components/NotarySection';

import UploadDocument from 'modules/documents/components/UploadDocument';
import { useAddEncumbranceModal } from '../context/AddEncumbranceModalContext';

const AddEncumbranceModalBody: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useAddEncumbranceModal();

  const isShareRangeValid =
    data.from >= (data.shareFrom || 0) &&
    data.to <= (data.shareTo || 0) &&
    data.from <= data.to;

  const affectedShares = data.to - data.from + 1;

  const handleChangeNotaryName = (value: string) => {
    const scriptureData = {
      ...data.scriptureData,
      notaryName: value,
    };
    actions.handleChangeScriptureData(scriptureData);
  };

  const handleChangeNotaryProtocol = (value: string) => {
    const scriptureData = {
      ...data.scriptureData,
      protocolNr: value,
    };
    actions.handleChangeScriptureData(scriptureData);
  };

  const handleChangeNotaryCity = (value: string) => {
    const scriptureData = {
      ...data.scriptureData,
      notaryCity: value,
    };
    actions.handleChangeScriptureData(scriptureData);
  };

  return (
    <Modal.Body>
      <div className="form-group row">
        <div className="col-4">
          <label className="form-label" htmlFor="encumbrance-name">
            {t('EncumbranceType')} <MandatoryField />
          </label>

          <Select
            id="encumbrance-name"
            closeMenuOnSelect
            className="react-select react-select-lg"
            value={data.selectedEncumbranceTypeOption}
            options={data.encumbranceTypeOptions}
            placeholder={t('SelectEncumbrance')}
            onChange={actions.handleChangeEncumbranceTypeOption}
            isDisabled={data.modalActionType === actionTypes.EDIT}
          />
        </div>

        <div className="form-group col-4">
          <InputDate
            id="start-date"
            label={t('StartDate')}
            value={data.date || ''}
            errorMessageKey="InvalidDate"
            onChange={actions.handleChangeDate}
            required
          />
        </div>

        <div className="form-group col-4">
          <InputDate
            id="end-date"
            label={t('EndDate')}
            value={data.endDate || ''}
            minDate={data.date || undefined}
            onChange={actions.handleChangeEndDate}
          />
        </div>
      </div>

      <div className="form-group row">
        <div className="col-4">
          <InputNumber
            id="encumbrance-from"
            label={t('From')}
            value={data.from}
            onChange={actions.handleChangeFrom}
            infoLabel={!isShareRangeValid ? t('InvalidRange') : undefined}
            required
          />
        </div>

        <div className="col-4">
          <InputNumber
            id="encumbrance-to"
            label={t('To')}
            value={data.to}
            onChange={actions.handleChangeTo}
            required
          />
        </div>

        <div className="col-4">
          <InputNumber
            id="affected-share"
            label={t('AffectedShares')}
            value={affectedShares}
            onChange={() => {}}
            readOnly
          />
        </div>
      </div>

      {data.modalActionType === actionTypes.ADD && (
        <div className="mb-4">
          <UploadDocument
            label="Scripture"
            inputLabel={data.file?.name || t('DragDocumentToUpload')}
            file={data.file}
            setFile={actions.handleChangeFile}
          />
        </div>
      )}

      <div className="row">
        <NotarySection
          notaryName={data.scriptureData.notaryName}
          onChangeName={handleChangeNotaryName}
          notaryProtocol={data.scriptureData.protocolNr}
          onChangeProtocol={handleChangeNotaryProtocol}
          notaryCity={data.scriptureData.notaryCity}
          onChangeCity={handleChangeNotaryCity}
          lgSize
        />
      </div>
    </Modal.Body>
  );
};

export default AddEncumbranceModalBody;
